import type { ApiClient, RequestOptions } from './client.ts';

/**
 * OpenAPI yollarından tipli çağrılar (08 §11.4): yol, yol ve sorgu parametreleri, gövde ve yanıt üretilen tiplerden
 * gelir. Olmayan yol, eksik parametre ya da yanlış gövde derlenmez (FE-02, FE-05 sınıfı kaymalar).
 *
 * ```ts
 * const api = typed<paths>(client);
 * const context = await api.get('/api/v1/me/context');
 * await api.patch('/api/v1/org/departments/{id}', { path: { id }, body: { name } });
 * ```
 */
export type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'patch';

/** Yolun bu yöntemdeki işlemi (tanımsızsa `never`). */
export type OperationOf<P, K extends keyof P, M extends HttpMethod> = P[K] extends { [X in M]: infer O } ? O : never;

/** Bu yöntemi destekleyen yollar. */
export type PathsWith<P, M extends HttpMethod> = {
  [K in keyof P]-?: P[K] extends { [X in M]: infer O } ? ([O] extends [undefined] ? never : K) : never;
}[keyof P] &
  string;

type Json<R> = R extends { content: { 'application/json': infer B } } ? B : undefined;
type ResponsesOf<O> = O extends { responses: infer R } ? R : never;
type SuccessStatus = 200 | 201 | 202 | 203 | 204;

/** Başarılı yanıtın gövdesi (204'te `undefined`). */
export type ResponseOf<O> = {
  [S in keyof ResponsesOf<O>]: S extends SuccessStatus ? Json<ResponsesOf<O>[S]> : never;
}[keyof ResponsesOf<O>];

type ParametersOf<O> = O extends { parameters: infer X } ? X : never;
export type PathParamsOf<O> = ParametersOf<O> extends { path?: infer X } ? NonNullable<X> : never;
export type QueryOf<O> = ParametersOf<O> extends { query?: infer X } ? NonNullable<X> : never;

type ContentOf<O> = O extends { requestBody?: infer B } ? (NonNullable<B> extends { content: infer C } ? C : never) : never;

/** İstek gövdesi: JSON ya da JSON Merge Patch şeması. */
export type BodyOf<O> =
  ContentOf<O> extends { 'application/json': infer B }
    ? B
    : ContentOf<O> extends { 'application/merge-patch+json': infer B }
      ? B
      : never;

type PathPart<O> = [PathParamsOf<O>] extends [never] ? { path?: undefined } : { path: PathParamsOf<O> };
type QueryPart<O> = [QueryOf<O>] extends [never]
  ? { query?: undefined }
  : object extends QueryOf<O>
    ? { query?: QueryOf<O> }
    : { query: QueryOf<O> };
type BodyPart<O> = [BodyOf<O>] extends [never] ? { body?: undefined } : { body: BodyOf<O> };

type CommonOptions = Pick<RequestOptions, 'headers' | 'signal' | 'anonymous' | 'withCredentials'>;

export type RequestFor<O> = PathPart<O> & QueryPart<O> & BodyPart<O> & CommonOptions;

/** Zorunlu alanı olmayan istekte seçenekler verilmeyebilir. */
type ArgsFor<O> = object extends RequestFor<O> ? [init?: RequestFor<O>] : [init: RequestFor<O>];

type Call<P, M extends HttpMethod> = <K extends PathsWith<P, M>>(
  path: K,
  ...args: ArgsFor<OperationOf<P, K, M>>
) => Promise<ResponseOf<OperationOf<P, K, M>>>;

export interface TypedClient<P> {
  get: Call<P, 'get'>;
  post: Call<P, 'post'>;
  put: Call<P, 'put'>;
  patch: Call<P, 'patch'>;
  delete: Call<P, 'delete'>;
}

/** `{id}` gibi yol parametrelerini kodlayarak yerleştirir; eksik parametre programlama hatasıdır. */
export function fillPath(template: string, params: Readonly<Record<string, string | number>> | undefined): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) {
      throw new Error(`Yol parametresi eksik: ${name} (${template})`);
    }

    return encodeURIComponent(String(value));
  });
}

type LooseInit = {
  path?: Readonly<Record<string, string | number>>;
  query?: RequestOptions['query'];
  body?: unknown;
} & CommonOptions;

export function typed<P>(client: ApiClient): TypedClient<P> {
  const call =
    (method: NonNullable<RequestOptions['method']>) =>
    (path: string, init?: LooseInit): Promise<unknown> =>
      client.request(fillPath(path, init?.path), {
        method,
        query: init?.query,
        body: init?.body,
        headers: init?.headers,
        signal: init?.signal,
        anonymous: init?.anonymous,
        withCredentials: init?.withCredentials,
      });

  return {
    get: call('GET') as Call<P, 'get'>,
    post: call('POST') as Call<P, 'post'>,
    put: call('PUT') as Call<P, 'put'>,
    patch: call('PATCH') as Call<P, 'patch'>,
    delete: call('DELETE') as Call<P, 'delete'>,
  };
}
